import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtDateTime, fileToBase64, fmtCurrency, packagePaymentAmounts } from "@/lib/utils-app";
import { ArrowLeft, Plus, Trash2, User, X, Camera, Activity, Swords } from "lucide-react";
import ResponsivaDocumentField, {
  openResponsivaDocument,
  responsivaIsPdf,
} from "@/components/ResponsivaDocumentField";
import { subscriberPhotoSrc } from "@/components/SubscriberAvatar";
import { openMercadoPagoCheckout, pollPaymentStatus } from "@/lib/mercadoPagoCheckout";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const emptyRec = { weight_kg: "", height_cm: "", chest_cm: "", waist_cm: "", hip_cm: "", biceps_cm: "", thigh_cm: "", calf_cm: "", body_fat_pct: "", notes: "", date: new Date().toISOString().slice(0, 10) };
const emptyFight = { fight_date: new Date().toISOString().slice(0, 10), location: "", category: "", weight_kg: "", images: [] };

export default function AdminSubscriberDetail() {
  const { id } = useParams();
  const [sub, setSub] = useState(null);
  const [packages, setPackages] = useState([]);
  const [tab, setTab] = useState("info");
  const [recOpen, setRecOpen] = useState(false);
  const [rec, setRec] = useState(emptyRec);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaData, setMediaData] = useState({ data: "", type: "image", note: "", date: new Date().toISOString().slice(0, 10) });
  const [fightOpen, setFightOpen] = useState(false);
  const [fight, setFight] = useState(emptyFight);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: 0, method: "cash", payment_date: new Date().toISOString().slice(0, 10), period_days: 30, notes: "", type: "monthly" });
  const [onlineAvailable, setOnlineAvailable] = useState(false);
  const [pkgOpen, setPkgOpen] = useState(false);
  const [nextPackageId, setNextPackageId] = useState("");
  const [responsiblePhoto, setResponsiblePhoto] = useState(null);
  const [savingResponsible, setSavingResponsible] = useState(false);
  const stopPollRef = useRef(null);

  const load = useCallback(
    () =>
      api.get(`/admin/subscribers/${id}`).then((r) => {
        setSub(r.data);
        setResponsiblePhoto(r.data.responsible_photo || r.data.responsible_photo_thumb || null);
      }),
    [id],
  );
  useEffect(() => {
    load();
    api.get("/admin/packages").then((r) => setPackages(r.data || []));
    api.get("/admin/settings").then((r) => {
      const s = r.data || {};
      setOnlineAvailable(!!s.online_payments_licensed && !!s.mp_enabled);
    });
    return () => stopPollRef.current?.();
  }, [id, load]);

  const subscriberPkg = packages.find((p) => p.id === sub?.package_id) || sub?.package;
  const payAmounts = packagePaymentAmounts(subscriberPkg, payForm.type);

  useEffect(() => {
    if (!payOpen || payForm.method === "online") return;
    setPayForm((f) => ({ ...f, amount: payAmounts.total }));
  }, [payOpen, payForm.type, payForm.method, payAmounts.total]);

  const confirmTransfer = async (paymentId) => {
    try {
      await api.post(`/admin/payments/${paymentId}/confirm`);
      toast.success("Transferencia confirmada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al confirmar");
    }
  };

  const paymentStatusLabel = (p) => {
    if (p.status === "pending_confirmation") return "Pendiente transferencia";
    if (p.status === "confirmed" || !p.status) return "Confirmado";
    return p.status;
  };

  if (!sub) return <div className="text-white/40">Cargando…</div>;

  const addRecord = async () => {
    const body = Object.fromEntries(Object.entries(rec).map(([k, v]) => [k, ["notes", "date"].includes(k) ? v : (v === "" ? null : Number(v))]));
    body.date = new Date(body.date).toISOString();
    await api.post(`/admin/subscribers/${id}/progress`, body);
    toast.success("Registro agregado");
    setRecOpen(false);
    setRec(emptyRec);
    load();
  };

  const delRecord = async (rid) => {
    if (!window.confirm("Eliminar registro?")) return;
    await api.delete(`/admin/subscribers/${id}/progress/${rid}`);
    load();
  };

  const addMedia = async () => {
    if (!mediaData.data) { toast.error("Carga un archivo"); return; }
    const body = { ...mediaData, date: new Date(mediaData.date).toISOString() };
    await api.post(`/admin/subscribers/${id}/media`, body);
    toast.success("Multimedia agregada");
    setMediaOpen(false);
    setMediaData({ data: "", type: "image", note: "", date: new Date().toISOString().slice(0, 10) });
    load();
  };

  const delMedia = async (mid) => {
    if (!window.confirm("Eliminar multimedia?")) return;
    await api.delete(`/admin/subscribers/${id}/media/${mid}`);
    load();
  };

  const onMediaFile = async (f) => {
    if (!f) return;
    const type = f.type.startsWith("video") ? "video" : "image";
    setMediaData({ ...mediaData, type, data: await fileToBase64(f) });
  };

  const onFightImages = async (files) => {
    if (!files?.length) return;
    const added = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) continue;
      added.push(await fileToBase64(f));
    }
    if (!added.length) {
      toast.error("Selecciona imágenes válidas");
      return;
    }
    setFight((s) => ({ ...s, images: [...(s.images || []), ...added] }));
  };

  const removeFightImage = (idx) => {
    setFight((s) => ({ ...s, images: (s.images || []).filter((_, i) => i !== idx) }));
  };

  const addFight = async () => {
    if (!fight.fight_date) {
      toast.error("Indica la fecha de la pelea");
      return;
    }
    const body = {
      fight_date: new Date(fight.fight_date).toISOString(),
      location: fight.location || "",
      category: fight.category || "",
      weight_kg: fight.weight_kg === "" ? null : Number(fight.weight_kg),
      images: fight.images || [],
    };
    await api.post(`/admin/subscribers/${id}/fights`, body);
    toast.success("Pelea registrada");
    setFightOpen(false);
    setFight(emptyFight);
    load();
  };

  const delFight = async (fid) => {
    if (!window.confirm("¿Eliminar registro de pelea?")) return;
    await api.delete(`/admin/subscribers/${id}/fights/${fid}`);
    load();
  };

  const addPayment = async () => {
    try {
      if (payForm.method === "online") {
        const { data } = await api.post("/admin/payments/checkout", {
          subscriber_id: id,
          type: payForm.type,
          notes: payForm.notes,
          origin_url: window.location.origin,
        });
        if (!data?.checkout_url || !data?.session_id) {
          toast.error("No se pudo crear la sesion de pago");
          return;
        }
        const opened = openMercadoPagoCheckout(data.checkout_url, data.session_id);
        if (opened) {
          toast.info("Mercado Pago en otra pestaña. Al pagar, el registro se guardará aquí automáticamente.");
        } else {
          toast.warning("Abre el enlace de pago en una nueva pestaña (ventanas emergentes bloqueadas).");
          window.open(data.checkout_url, "_blank");
        }
        setPayOpen(false);
        stopPollRef.current?.();
        stopPollRef.current = pollPaymentStatus(
          (sid) => api.get(`/payments/checkout/status/${sid}`).then((r) => r.data),
          data.session_id,
          {
            onPaid: () => {
              toast.success("Pago en línea confirmado y registrado");
              load();
            },
            onFailed: () => toast.error("El pago no se completó"),
            onTimeout: () => toast.info("Si ya pagó, recarga la página o intenta de nuevo."),
          },
        );
        return;
      }
      const body = {
        ...payForm,
        subscriber_id: id,
        amount: Number(payForm.amount),
        period_days: Number(payForm.period_days),
        payment_date: new Date(payForm.payment_date).toISOString(),
      };
      await api.post("/admin/payments", body);
      toast.success(payForm.method === "transfer" ? "Transferencia registrada (pendiente de confirmación)" : "Pago registrado");
      setPayOpen(false);
      setPayForm({ amount: 0, method: "cash", payment_date: new Date().toISOString().slice(0, 10), period_days: 30, notes: "", type: "monthly" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el pago");
    }
  };

  const saveResponsiblePhoto = async () => {
    if (!sub?.id) return;
    setSavingResponsible(true);
    try {
      const initial = sub.responsible_photo || null;
      const payload = {
        full_name: sub.full_name,
        username: sub.username,
        active: sub.active !== false,
        fitness_goal: sub.fitness_goal || "",
      };
      if (responsiblePhoto !== initial) {
        payload.responsible_photo = responsiblePhoto || null;
      }
      await api.put(`/admin/subscribers/${sub.id}`, payload);
      toast.success("Carta responsiva actualizada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingResponsible(false);
    }
  };

  const schedulePackageChange = async () => {
    if (!nextPackageId) {
      toast.error("Selecciona un paquete");
      return;
    }
    try {
      await api.put(`/admin/subscribers/${id}/package-change`, { package_id: nextPackageId });
      toast.success("Cambio de paquete programado para próximo ciclo");
      setPkgOpen(false);
      setNextPackageId("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo programar el cambio");
    }
  };

  const chartData = (sub.progress_records || []).map(r => ({
    date: new Date(r.date).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }),
    Peso: r.weight_kg,
    Cintura: r.waist_cm,
    Pecho: r.chest_cm,
    Cadera: r.hip_cm,
  }));

  return (
    <div data-testid="subscriber-detail-page">
      <Link to="/admin/suscriptores" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1 mb-4"><ArrowLeft size={14} /> Volver</Link>
      <div className="glass p-6 rounded-sm flex flex-wrap items-center gap-6">
        <div className="h-24 w-24 rounded-full overflow-hidden bg-white/5 grid place-items-center shrink-0">
          {subscriberPhotoSrc(sub, { preferThumb: false }) ? (
            <img src={subscriberPhotoSrc(sub, { preferThumb: false })} className="h-full w-full object-cover" alt="" />
          ) : (
            <User size={36} className="text-white/40" />
          )}
        </div>
        <div className="flex-1 min-w-[240px]">
          <h1 className="font-display text-4xl text-white">{sub.full_name}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/60 mt-1">
            <span>📧 {sub.email || "—"}</span>
            <span>📞 {sub.phone || "—"}</span>
            <span>📅 Inscrito {fmtDate(sub.enrolled_at)}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {sub.package && <span className="badge-status badge-valid">{sub.package.name}</span>}
            {sub.pending_package && <span className="badge-status badge-soon">Siguiente ciclo: {sub.pending_package.name}</span>}
            <span className={`badge-status ${sub.payment_status === "valid" ? "badge-valid" : sub.payment_status === "expiring_soon" ? "badge-soon" : sub.payment_status === "overdue" ? "badge-overdue" : "bg-white/5 text-white/40 border border-white/10"}`}>
              {sub.payment_status === "valid" ? "Al día" : sub.payment_status === "expiring_soon" ? "Por vencer" : sub.payment_status === "overdue" ? "Vencido" : "Sin pagos"}
            </span>
          </div>
        </div>
        <button onClick={() => { setNextPackageId(sub.pending_package?.id || ""); setPkgOpen(true); }} className="btn-outline text-xs py-2">Cambiar paquete</button>
      </div>

      <div className="mt-6 border-b border-theme-secondary/10 flex gap-1 overflow-x-auto">
        {[
          { k: "info", l: "Información" },
          { k: "progress", l: "Progreso físico" },
          { k: "fights", l: "Peleas" },
          { k: "media", l: "Galería" },
          { k: "payments", l: "Pagos" },
          { k: "attendance", l: "Asistencias" },
        ].map(t => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm uppercase tracking-widest font-display ${tab === t.k ? "text-white border-b-2 border-[#E63946]" : "text-white/40 hover:text-white"}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "info" && (
          <div className="space-y-6">
            <div className="glass p-5 rounded-sm max-w-xl">
              <h3 className="font-display text-xl text-white mb-3">Carta responsiva</h3>
              <p className="text-xs text-white/40 mb-4">
                Acuerdo firmado entre lo contratado y lo que el suscriptor espera del servicio.
              </p>
              <ResponsivaDocumentField
                value={responsiblePhoto}
                onChange={setResponsiblePhoto}
              />
              <button
                type="button"
                onClick={saveResponsiblePhoto}
                disabled={savingResponsible}
                className="btn-primary text-sm py-2 mt-4"
              >
                {savingResponsible ? "Guardando…" : "Guardar carta responsiva"}
              </button>
            </div>
            {(sub.responsible_photo || sub.has_responsible_photo) && (
              <div className="glass p-4 rounded-sm max-w-xl flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/40">Documento en archivo</div>
                  <div className="text-white mt-1">
                    {responsivaIsPdf(sub.responsible_photo) ? "PDF firmado" : "Imagen escaneada"}
                  </div>
                </div>
                {sub.responsible_photo && (
                  <button
                    type="button"
                    onClick={() => openResponsivaDocument(sub.responsible_photo)}
                    className="btn-outline text-xs py-2 shrink-0"
                  >
                    Abrir original
                  </button>
                )}
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
            {[
              ["Género", sub.gender],
              ["Nacimiento", fmtDate(sub.birth_date)],
              ["Contacto emergencia", sub.emergency_contact_name],
              ["Tel. emergencia", sub.emergency_contact_phone],
              ["Objetivo", sub.fitness_goal || "—"],
              ["Vencimiento", fmtDate(sub.next_due_date)],
              ["Beneficios paquete", (sub.package?.benefits || []).length ? (sub.package.benefits.join(", ")) : "—"],
              ["Turnos del paquete", (sub.allowed_shifts || []).length
                ? sub.allowed_shifts.map((s) => `${s.name} (${s.start_time}–${s.end_time})`).join(" · ")
                : "—"],
              ["Cambio pendiente", sub.pending_package?.name || "Sin cambio programado"],
            ].map(([k, v], i) => (
              <div key={i} className="glass p-4 rounded-sm">
                <div className="text-xs uppercase tracking-widest text-white/40">{k}</div>
                <div className="text-white mt-1">{v || "—"}</div>
              </div>
            ))}
            </div>
          </div>
        )}

        {tab === "progress" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-2xl text-white">Registros físicos</h3>
              <button onClick={() => setRecOpen(true)} className="btn-primary text-sm" data-testid="new-progress-btn"><Plus size={14} /> Nuevo registro</button>
            </div>
            {chartData.length > 1 && (
              <div className="glass p-4 rounded-sm mb-4">
                <div className="text-xs uppercase tracking-widest text-white/40 mb-2"><Activity className="inline" size={12} /> Evolución</div>
                <div className="h-72 min-w-0">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                      <XAxis dataKey="date" tick={{ fill: "#888", fontSize: 11 }} />
                      <YAxis tick={{ fill: "#888", fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #222", color: "#fff" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="Peso" stroke="#E63946" strokeWidth={2} />
                      <Line type="monotone" dataKey="Cintura" stroke="#FFD700" strokeWidth={2} />
                      <Line type="monotone" dataKey="Pecho" stroke="#22C55E" strokeWidth={2} />
                      <Line type="monotone" dataKey="Cadera" stroke="#3B82F6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            <div className="glass rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-theme-secondary/10">
                    <th className="p-3">Fecha</th><th className="p-3">Peso</th><th className="p-3">Cintura</th><th className="p-3">Pecho</th><th className="p-3">Cadera</th><th className="p-3">Bíceps</th><th className="p-3">%Grasa</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {(sub.progress_records || []).map(r => (
                    <tr key={r.id} className="border-b border-theme-secondary/10 text-white/60">
                      <td className="p-3">{fmtDate(r.date)}</td>
                      <td className="p-3">{r.weight_kg ?? "—"} kg</td>
                      <td className="p-3">{r.waist_cm ?? "—"} cm</td>
                      <td className="p-3">{r.chest_cm ?? "—"} cm</td>
                      <td className="p-3">{r.hip_cm ?? "—"} cm</td>
                      <td className="p-3">{r.biceps_cm ?? "—"} cm</td>
                      <td className="p-3">{r.body_fat_pct ?? "—"} %</td>
                      <td className="p-3 text-right"><button onClick={() => delRecord(r.id)} className="text-red-400"><Trash2 size={14} /></button></td>
                    </tr>
                  ))}
                  {(sub.progress_records || []).length === 0 && <tr><td colSpan={8} className="text-center py-10 text-white/40">Sin registros</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "fights" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-2xl text-white">Historial de peleas</h3>
              <button onClick={() => setFightOpen(true)} className="btn-primary text-sm" data-testid="new-fight-btn">
                <Plus size={14} /> Registrar pelea
              </button>
            </div>
            <div className="space-y-4">
              {(sub.fight_records || []).map((f) => (
                <div key={f.id} className="glass rounded-sm p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-xl text-white flex items-center gap-2">
                        <Swords size={18} className="text-[#E63946]" />
                        {fmtDate(f.fight_date)}
                      </div>
                      <div className="mt-2 grid sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-sm text-white/60">
                        <span><span className="text-white/40">Lugar:</span> {f.location || "—"}</span>
                        <span><span className="text-white/40">Categoría:</span> {f.category || "—"}</span>
                        <span><span className="text-white/40">Peso:</span> {f.weight_kg != null ? `${f.weight_kg} kg` : "—"}</span>
                      </div>
                    </div>
                    <button onClick={() => delFight(f.id)} className="text-red-400 shrink-0">
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {(f.images || []).length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {f.images.map((img, i) => (
                        <img key={i} src={img} alt={`Pelea ${i + 1}`} className="h-28 w-28 object-cover rounded-sm border border-white/10" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {(sub.fight_records || []).length === 0 && (
                <div className="glass p-10 text-center text-white/40">Sin peleas registradas</div>
              )}
            </div>
          </>
        )}

        {tab === "media" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-2xl text-white">Galería cronológica</h3>
              <button onClick={() => setMediaOpen(true)} className="btn-primary text-sm" data-testid="new-media-btn"><Camera size={14} /> Agregar</button>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {(sub.media || []).map(m => (
                <div key={m.id} className="glass rounded-sm overflow-hidden">
                  {m.type === "image" ? (
                    <img src={m.data} className="w-full h-48 object-cover" />
                  ) : (
                    <video src={m.data} controls className="w-full h-48 object-cover bg-black" />
                  )}
                  <div className="p-3">
                    <div className="text-xs text-white/40">{fmtDate(m.date)}</div>
                    <div className="text-sm text-white mt-1 line-clamp-2">{m.note}</div>
                    <button onClick={() => delMedia(m.id)} className="text-xs text-red-400 mt-2"><Trash2 className="inline" size={12} /> Eliminar</button>
                  </div>
                </div>
              ))}
              {(sub.media || []).length === 0 && <div className="md:col-span-3 lg:col-span-4 glass p-10 text-center text-white/40">No hay multimedia aún.</div>}
            </div>
          </>
        )}

        {tab === "attendance" && (
          <>
            <h3 className="font-display text-2xl text-white mb-4">Historial de asistencias</h3>
            <div className="glass rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[480px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-theme-secondary/10">
                    <th className="p-3">Asistencia</th>
                    <th className="p-3">Turno</th>
                  </tr>
                </thead>
                <tbody>
                  {(sub.attendance_records || [])
                    .filter((a) => a.counts_as_entry !== false)
                    .map((a) => (
                    <tr key={a.id} className="border-b border-theme-secondary/10 text-white/60">
                      <td className="p-3 tabular-nums">{fmtDateTime(a.check_in_at)}</td>
                      <td className="p-3">{a.shift_name || "—"}</td>
                    </tr>
                  ))}
                  {(sub.attendance_records || []).filter((a) => a.counts_as_entry !== false).length === 0 && (
                    <tr>
                      <td colSpan={2} className="text-center py-10 text-white/40">
                        Sin asistencias registradas
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "payments" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-2xl text-white">Historial de pagos</h3>
              <button onClick={() => setPayOpen(true)} className="btn-primary text-sm" data-testid="new-payment-btn"><Plus size={14} /> Registrar pago</button>
            </div>
            <div className="glass rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-theme-secondary/10">
                    <th className="p-3">Fecha</th><th className="p-3">Tipo</th><th className="p-3">Método</th><th className="p-3">Estado</th><th className="p-3">Monto</th><th className="p-3">Próximo vto.</th><th className="p-3">Notas</th><th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {(sub.payments || []).map(p => (
                    <tr key={p.id} className="border-b border-theme-secondary/10 text-white/60">
                      <td className="p-3">{fmtDate(p.payment_date)}</td>
                      <td className="p-3 capitalize">{p.type}</td>
                      <td className="p-3 capitalize">{p.method}</td>
                      <td className="p-3">
                        <span className={p.status === "pending_confirmation" ? "text-amber-400" : "text-green-400/80"}>
                          {paymentStatusLabel(p)}
                        </span>
                      </td>
                      <td className="p-3 text-[#E63946] font-bold">{fmtCurrency(p.amount)}</td>
                      <td className="p-3">{fmtDate(p.next_due_date)}</td>
                      <td className="p-3 text-white/60">{p.notes}</td>
                      <td className="p-3 text-right">
                        {p.method === "transfer" && p.status === "pending_confirmation" && (
                          <button type="button" onClick={() => confirmTransfer(p.id)} className="btn-primary text-xs py-1">
                            Confirmar transferencia
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(sub.payments || []).length === 0 && <tr><td colSpan={8} className="text-center py-10 text-white/40">Sin pagos</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {recOpen && (
        <Modal title="Nuevo registro físico" onClose={() => setRecOpen(false)} onSave={addRecord}>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Fecha"><input type="date" className="input-dark mt-1" value={rec.date} onChange={(e) => setRec({ ...rec, date: e.target.value })} /></Field>
            <Field label="Peso (kg)"><input type="number" step="0.1" className="input-dark mt-1" value={rec.weight_kg} onChange={(e) => setRec({ ...rec, weight_kg: e.target.value })} /></Field>
            <Field label="Altura (cm)"><input type="number" step="0.1" className="input-dark mt-1" value={rec.height_cm} onChange={(e) => setRec({ ...rec, height_cm: e.target.value })} /></Field>
            <Field label="Pecho (cm)"><input type="number" step="0.1" className="input-dark mt-1" value={rec.chest_cm} onChange={(e) => setRec({ ...rec, chest_cm: e.target.value })} /></Field>
            <Field label="Cintura (cm)"><input type="number" step="0.1" className="input-dark mt-1" value={rec.waist_cm} onChange={(e) => setRec({ ...rec, waist_cm: e.target.value })} /></Field>
            <Field label="Cadera (cm)"><input type="number" step="0.1" className="input-dark mt-1" value={rec.hip_cm} onChange={(e) => setRec({ ...rec, hip_cm: e.target.value })} /></Field>
            <Field label="Bíceps (cm)"><input type="number" step="0.1" className="input-dark mt-1" value={rec.biceps_cm} onChange={(e) => setRec({ ...rec, biceps_cm: e.target.value })} /></Field>
            <Field label="Muslo (cm)"><input type="number" step="0.1" className="input-dark mt-1" value={rec.thigh_cm} onChange={(e) => setRec({ ...rec, thigh_cm: e.target.value })} /></Field>
            <Field label="Pantorrilla (cm)"><input type="number" step="0.1" className="input-dark mt-1" value={rec.calf_cm} onChange={(e) => setRec({ ...rec, calf_cm: e.target.value })} /></Field>
            <Field label="% Grasa corporal"><input type="number" step="0.1" className="input-dark mt-1" value={rec.body_fat_pct} onChange={(e) => setRec({ ...rec, body_fat_pct: e.target.value })} /></Field>
          </div>
          <Field label="Notas"><textarea rows={2} className="input-dark mt-1 resize-none" value={rec.notes} onChange={(e) => setRec({ ...rec, notes: e.target.value })} /></Field>
        </Modal>
      )}

      {fightOpen && (
        <Modal title="Registrar pelea" onClose={() => { setFightOpen(false); setFight(emptyFight); }} onSave={addFight}>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Fecha de pelea">
              <input type="date" className="input-dark mt-1" value={fight.fight_date} onChange={(e) => setFight({ ...fight, fight_date: e.target.value })} />
            </Field>
            <Field label="Peso (kg)">
              <input type="number" step="0.1" className="input-dark mt-1" value={fight.weight_kg} onChange={(e) => setFight({ ...fight, weight_kg: e.target.value })} placeholder="Ej. 75.5" />
            </Field>
            <Field label="Lugar de pelea">
              <input className="input-dark mt-1" value={fight.location} onChange={(e) => setFight({ ...fight, location: e.target.value })} placeholder="Ciudad, venue, etc." />
            </Field>
            <Field label="Categoría">
              <input className="input-dark mt-1" value={fight.category} onChange={(e) => setFight({ ...fight, category: e.target.value })} placeholder="Ej. Welter, Novatos, etc." />
            </Field>
          </div>
          <Field label="Imágenes">
            <input type="file" accept="image/*" multiple className="input-dark mt-1" onChange={(e) => { onFightImages(e.target.files); e.target.value = ""; }} />
            {(fight.images || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {fight.images.map((img, i) => (
                  <div key={i} className="relative">
                    <img src={img} alt="" className="h-24 w-24 object-cover rounded-sm border border-white/10" />
                    <button type="button" onClick={() => removeFightImage(i)} className="absolute -top-2 -right-2 bg-red-600 rounded-full p-0.5 text-white">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </Modal>
      )}

      {mediaOpen && (
        <Modal title="Agregar multimedia" onClose={() => setMediaOpen(false)} onSave={addMedia}>
          <Field label="Archivo (imagen o video)"><input type="file" accept="image/*,video/*" className="input-dark mt-1" onChange={(e) => onMediaFile(e.target.files?.[0])} /></Field>
          {mediaData.data && mediaData.type === "image" && <img src={mediaData.data} className="max-h-48 rounded-sm" />}
          {mediaData.data && mediaData.type === "video" && <video src={mediaData.data} controls className="max-h-48" />}
          <Field label="Fecha"><input type="date" className="input-dark mt-1" value={mediaData.date} onChange={(e) => setMediaData({ ...mediaData, date: e.target.value })} /></Field>
          <Field label="Nota"><textarea rows={2} className="input-dark mt-1 resize-none" value={mediaData.note} onChange={(e) => setMediaData({ ...mediaData, note: e.target.value })} placeholder="Antes / después / observación" /></Field>
        </Modal>
      )}

      {payOpen && (
        <Modal title="Registrar pago" onClose={() => setPayOpen(false)} onSave={addPayment}>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Tipo"><select className="input-dark mt-1" value={payForm.type} onChange={(e) => setPayForm({ ...payForm, type: e.target.value })}><option value="monthly">Mensualidad</option><option value="inscription">Inscripción</option></select></Field>
            <Field label="Método">
              <select className="input-dark mt-1" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
                <option value="cash">Efectivo</option>
                <option value="transfer">Transferencia</option>
                {onlineAvailable && <option value="online">En línea</option>}
              </select>
            </Field>
            {payForm.type === "inscription" && subscriberPkg && (
              <div className="md:col-span-2 text-sm text-white/60 bg-white/5 p-3 rounded-sm space-y-1">
                <div>Inscripción: {subscriberPkg.inscription_free ? "Gratis" : fmtCurrency(payAmounts.inscription_part)}</div>
                <div>Mensualidad: {fmtCurrency(payAmounts.monthly_part)}</div>
                <div className="text-[#FFD700] font-medium">Total: {fmtCurrency(payAmounts.total)}</div>
              </div>
            )}
            <Field label="Monto">
              <input
                type="number"
                step="0.01"
                className="input-dark mt-1"
                value={payForm.method === "online" ? (sub?.package?.monthly_cost || 0) : payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                disabled={payForm.method === "online"}
              />
            </Field>
            <Field label="Fecha de pago"><input type="date" className="input-dark mt-1" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></Field>
            <Field label="Periodo (días)"><input type="number" className="input-dark mt-1" value={payForm.period_days} onChange={(e) => setPayForm({ ...payForm, period_days: e.target.value })} /></Field>
          </div>
          <Field label="Notas"><textarea rows={2} className="input-dark mt-1 resize-none" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} /></Field>
          {payForm.method === "transfer" && (
            <p className="text-xs text-amber-400/90">La transferencia quedará pendiente hasta que confirmes que llegó el depósito.</p>
          )}
          {payForm.method === "online" && (
            <p className="text-xs text-white/60">
              Se abrirá Mercado Pago en otra pestaña. No hace falta volver desde la pantalla verde: al acreditarse el cobro, el pago se registra aquí solo.
            </p>
          )}
        </Modal>
      )}

      {pkgOpen && (
        <Modal title="Programar cambio de paquete" onClose={() => setPkgOpen(false)} onSave={schedulePackageChange}>
          <Field label="Nuevo paquete (aplica en próximo ciclo)">
            <select className="input-dark mt-1" value={nextPackageId} onChange={(e) => setNextPackageId(e.target.value)}>
              <option value="">Selecciona…</option>
              {packages.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>
          <p className="text-xs text-white/60">
            El cambio no modifica el ciclo actual. Se aplicará al registrar el siguiente pago mensual.
          </p>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/60">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, children, onClose, onSave }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="glass-strong max-w-2xl w-full rounded-sm my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-theme-secondary/10">
          <div className="font-display text-2xl text-white">{title}</div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X /></button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
        <div className="p-5 border-t border-theme-secondary/10 flex justify-end gap-3">
          <button onClick={onClose} className="btn-outline text-sm py-2">Cancelar</button>
          <button onClick={onSave} className="btn-primary text-sm py-2">Guardar</button>
        </div>
      </div>
    </div>
  );
}
